// Moved from `src/hooks/mutation/useMutateUpdateUserDefaultTranscribeMode.ts`
// (now the app delegate) with the identity D2 slice (ARCFE-1062). Unblocked by
// D1 (ARCFE-1037), which carved `updateUser` into `../api/user`.
//
// Landed in THIS family rather than dictation by dictation's own D3 ruling: the
// mode is a field on the user record and the write is PATCH /user/.
//
// Severed per ARCFE-921: the destructive toast. `invalidateCurrentUser()`
// crosses as a port for the OT4 reason — it is COMPOSITE (GET_USER *and* the
// commerce GET_PRODUCTS behind one try/catch), so reimplementing only its
// workspace leg here would silently drop a commerce invalidation.
//
// `useQueryUser` is read from this package's own `../query/useQueryUser` rather
// than through the app re-export the legacy file imported. Same hook: since IP2
// `src/hooks/query/useQueryUser.ts` re-exports it from `@heidi/workspace/data`,
// so the value is identical and no fetch moved (the read surface is projected
// from the app-owned `CurrentUserProvider`).
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type TranscribeMode } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_USER, type UserResponse } from '../../contracts';
import { updateUser } from '../api/user';
import { useQueryUser } from '../query/useQueryUser';

const UPDATE_USER_DEFAULT_TRANSCRIBE_MODE = 'UpdateUserDefaultTranscribeMode';

export interface UseMutateUpdateUserDefaultTranscribeModeOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onTranscribeModeUpdated?: () => void;
  /**
   * App-reaction port: fired where the legacy destructive toast fired — AFTER
   * the optimistic rollback and the log, preserving the frozen ordering.
   *
   * Receives the error. The toast-raising consumer ignores it, but the desktop
   * reconnect replay needs the HTTP status to tell a retryable blip from a
   * permanent 4xx it should stop re-attempting.
   */
  onTranscribeModeError?: (error: unknown) => void;
}

/** PATCH /user/ — writes `session_settings.last_recording_mode`. */
export function useMutateUpdateUserDefaultTranscribeMode(
  options?: UseMutateUpdateUserDefaultTranscribeModeOptions
) {
  const { data: userData } = useQueryUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_USER_DEFAULT_TRANSCRIBE_MODE],
    // Serialize every writer of this field, across hook instances. Two of them
    // exist: the clinician's own mode picker, and the desktop reconnect replay
    // that flushes a mode chosen while offline. Unscoped they are independent
    // in-flight requests, so a replay carrying an older value could land AFTER
    // a newer pick and leave the account default stale. Scoped, the second
    // waits for the first, and last-write-wins matches call order.
    scope: { id: UPDATE_USER_DEFAULT_TRANSCRIBE_MODE },
    mutationFn: async (mode: TranscribeMode) => {
      if (mode && userData) {
        await updateUser({
          session_settings: {
            ...userData.session_settings,
            default_template_id: userData.session_settings?.default_template_id || null,
            last_recording_mode: mode,
          },
        });
      }
    },
    onMutate: async (mode: TranscribeMode) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      queryClient.setQueryData<UserResponse>([GET_USER], (oldUser) => {
        if (!oldUser?.session_settings) {
          return oldUser;
        }

        return {
          ...oldUser,
          session_settings: {
            ...(oldUser.session_settings ?? {}),
            last_recording_mode: mode,
          },
        };
      });

      return { previousUser };
    },
    onSuccess: () => {
      options?.onTranscribeModeUpdated?.();
    },
    onError: (error, _mode, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData<UserResponse>([GET_USER], context.previousUser);
      }
      // Sanitized, not the raw error. The Datadog transport does not apply
      // pino's redaction and error-level logs are forwarded in prod, so an
      // axios error would carry the request URL and response body with it.
      logger.error({
        msg: 'Failed to update the user default transcribe mode',
        ...sanitizeError(error),
      });
      options?.onTranscribeModeError?.(error);
    },
  });
}
